<template>
  <div @click="sendToHeap()" v-bind="$attrs">
    <router-link
      :target="openNewTab"
      v-if="!loading"
      :to="{ name: 'trip-plan', params: { trip: props.data?.url } }"
      class="trip-plan-link"
      :class="[version === 'primary' ? 'primary' : '']"
    >
      <div class="trip-plan-card overflow-hidden br-16" :class="[version === 'primary' ? 'primary' : 'hand']">
        <div class="relative">
          <div class="trip-plan-card__image hp-100 h-140">
            <LazyLoad when-visible class="el-cover overflow-hidden maxh-140" :distance="0">
              <BaseResponsiveImages :src="data?.featured_image" :alt="data?.name" />
            </LazyLoad>
          </div>
          <div class="maxwp-50 destination flex items-center mb-4 absolute bottom-12 left-12 bg-white pl-6 pr-8 pt-2 pb-2 br-999">
            <img :src="`${ASSETS_CDN}/icons/location_on.svg`" width="14" height="14" alt="location icon" />
            <span class="fs-14 fw-500 lh-20 ml-4 truncate truncate-1">{{ data.destinations }}</span>
          </div>
        </div>

        <div class="trip-plan-card__content pa-12">
          <div class="flex flex-column grid-gap-8">
            <div class="trip-plan-card__content-data flex flex-column grid-gap-4">
              <div class="trip-plan-card__title title fw-700 fs-20 lh-26 ellipsis truncate truncate-2 h-52">
                {{ data.name }}
              </div>
              <div class="overview fs-16 ellipsis truncate truncate-3 lh-24" v-html="data?.overview" v-if="version === 'primary'"></div>
              <div class="dib v-mid h-16">
                <div class="relative dib v-mid overflow-hidden">
                  <Rate v-if="data?.designer.rating" :rate="data?.designer.rating" :size="12" />
                </div>
                <div class="dib ml-4 number-reviews gray-700 fs-12 lh-16" v-if="review">
                  {{ review }}
                </div>
              </div>
            </div>
            <div v-if="data.activity_types?.length" class="trip-plan-card__content-activities flex flex-column grid-gap-8 relative">
              <div class="fs-14 fw-400 lh-20 ellipsis truncate truncate-2 gray-700 mb-dn h-40">
                {{ data.overview }}
              </div>
              <div class="flex justify-between bd-b-1 bd-gray-300-new pb-8">
                <div>
                  <div class="fs-14 fw-400 lh-20 gray-700">Starting from</div>
                  <div class="fs-20 fw-700 lh-28 ls-4">{{ calculatedPrice }}</div>
                </div>
              </div>

              <div class="trip-plan-card__content-activities-list flex flex-nowrap grid-gap-4 relative">
                <ActivitySuggested v-for="(item, index) in data?.activity_types" :key="index" :text="item" :showLink="false" version="sub" />
              </div>
              <div class="overlay nright-27 bottom-0 w-118 h-35 bg-linear-gradient-5 absolute"></div>
            </div>
            <div class="trip-plan-card__content-confidence flex flex-column grid-gap-8" v-if="version === 'primary'">
              <div class="title fs-16 fw-700 lh-24 gray-900">Book with Confidence</div>

              <TripPlanCardConfidence v-for="item in confidence" :key="item" :label="item" />
            </div>

            <router-link
              v-if="version === 'primary'"
              class="button button-yellow-score"
              :to="{
                name: 'enquiry-submit-trip',
                params: { trip: data?.url },
              }"
            >
              Customise This Trip
            </router-link>
          </div>
        </div>
      </div>
    </router-link>
  </div>
</template>

<script setup lang="ts">
import LazyLoad from '~/components/ui/lazy-load/LazyLoad'
import BaseResponsiveImages from '~/components/ui/BaseResponsiveImages/BaseResponsiveImages'
import { assetCDN, formatCurrency, USDToCurrency, CurrencyToUSD } from '~/helper'
import { debounceHeapTracking } from '~/mixins/heap'
import { debounceAmplitudeScriptTracking } from '~/mixins/amplitude'
import TripPlanCardConfidence from '~/components/pages/Search/components/TripPlanCardConfidence'
import ActivitySuggested from '~/components/sections/SectionGlobalHome/NewSearchBox/ActivitySuggested'
import { computed } from 'vue'
import { DESKTOP } from '~/constant/common'
import Rate from '~/components/ui/rate/Rate.vue'
import { useCurrenciesStore } from '~/store/pinia/currencies'
import { useGeneralInfoStore } from '~/store/pinia/generalInfo'

const props = defineProps({
  data: {
    type: Object,
    // required: true,
  },
  version: {
    type: String,
    required: false,
    default: 'primary',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  order: { type: Number, default: 0 },
  isDefaultPage: {
    type: Boolean,
    default: false,
  },
})
const ASSETS_CDN = assetCDN()
const confidence = ['Flexible Payment', 'Extra Mile']
const currenciesStore = useCurrenciesStore()
const generalStore = useGeneralInfoStore()

const currencySelected = computed(() => currenciesStore.currencySelected /*store.state.currenciesStore.currencySelected*/)
const review = computed(() => {
  const { data } = props
  if (data?.designer.review_count || data?.designer.number_of_reviews) {
    const count = data?.designer.review_count || data?.designer.number_of_reviews
    return `(${count} ${count === 1 ? 'review' : 'reviews'})`
  }
  return null
})
const originalPriceByUSD = computed(() => {
  const { rate } = currenciesData.value?.find(({ code }) => code === props.data.currency)
  return CurrencyToUSD(rate, props.data.price, false)
})

const calculatedPrice = computed(() => {
  const { rate, code, symbol } = currencySelected.value

  let priceFinal = ''
  // Update price if user change currency and difference from 'display_currency'
  if (code !== props?.data?.display_currency) {
    priceFinal = formatCurrency(
      USDToCurrency({
        rate: rate,
        volume: originalPriceByUSD.value,
        currency: code,
      }),
      symbol,
    )
  } else {
    priceFinal = formatCurrency(props?.data?.display_price, symbol)
  }
  return priceFinal
})

const currenciesData = computed(() => {
  // return store.state.currenciesStore.currenciesData
  return currenciesStore.currenciesData
})

const sendToHeap = () => {
  // console.log('Click Trip Plan Card: ',props.data?.url)
  debounceHeapTracking('Click Trip Plan Card', {
    trip_plan_path: props.data?.url,
    rank: props.order,
  })
  debounceAmplitudeScriptTracking('Click Trip Plan Card', {
    trip_plan_path: props.data?.url,
    rank: props.order,
  })
}

const windowWidth = computed(() => generalStore.generalData.windowWidth)

const openNewTab = computed(() => {
  return windowWidth.value > DESKTOP ? '_blank' : '_self'
})
</script>

<style lang="scss" scoped>
.trip-plan-link {
  color: black;
  flex: 1 0 390px;

  &.primary {
    flex: 1 0 500px;
  }

  @include phone() {
    flex: 1 0 100%;
  }
}
.trip-plan-card {
  flex: 1 0 390px;
  height: fit-content;
  min-height: 380px;
  border: 1px solid $gray-300-new;
  background: $white;
  transition: ease-out-back, 350ms;
  @include phone() {
    min-height: 332px;
  }

  &__image {
    transition-property: transform;
    transition-duration: 0.3s;
    transition-timing-function: cubic-bezier(0.5, 0, 0, 1);
  }

  &.primary {
    flex: 1;
    width: 500px;
    @include tablet() {
      width: 100%;
    }

    &__title {
      height: fit-content;
    }
  }

  &__title {
    height: 52px;
  }

  &__content {
    &-activities-list {
      max-height: 72px;
      overflow: hidden;
    }
  }

  &:hover {
    box-shadow: 0 4px 44px 0 rgba(0, 0, 0, 0.1);
    &__image {
      transform: scale(1.04);
    }
  }
}
.rating {
  background-repeat: repeat-x;
}
</style>
